import { Injectable } from 'framework/di';

import { BOOST_SD_COLLECTION_ID_KEY, BOOST_SD_COLLECTION_TAGS_KEY } from './filter.constants';

/**
 * Storage helper for filter module operations.
 *
 * Provides a unified interface for localStorage and sessionStorage operations
 * with built-in error handling, type safety, and JSON serialization. Manages
 * filter-related storage including collection data, pagination state, and
 * temporary filter selections.
 *
 * @remarks
 * This helper handles:
 * - localStorage get/set operations with null safety
 * - sessionStorage operations with JSON serialization/parsing
 * - Collection-specific storage (tags, IDs, handles)
 * - Pagination state persistence across sessions
 * - Storage availability detection
 * - Prefix-based bulk operations
 *
 * All storage operations include SSR-safe checks for storage availability.
 * JSON operations are wrapped in try-catch to handle parsing errors and
 * quota exceeded scenarios gracefully.
 *
 * Technical Support teams can extend this helper to add custom storage
 * operations, implement shop-specific caching strategies, or integrate
 * with alternative storage backends.
 *
 * @example
 * Extend to add custom storage encryption:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterStorageHelper', (FilterStorageHelper) => {
 *   return class CustomStorageHelper extends FilterStorageHelper {
 *     setLocalStorage(key, value) {
 *       // Encrypt sensitive data before storage
 *       const encrypted = this.encrypt(value);
 *       super.setLocalStorage(key, encrypted);
 *     }
 *
 *     getLocalStorage(key) {
 *       const encrypted = super.getLocalStorage(key);
 *       return encrypted ? this.decrypt(encrypted) : null;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override to add storage analytics:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterStorageHelper', (FilterStorageHelper) => {
 *   return class extends FilterStorageHelper {
 *     setSessionStorage(key, value) {
 *       super.setSessionStorage(key, value);
 *       // Track storage usage
 *       this.analytics.track('filter_storage_set', {
 *         key,
 *         size: JSON.stringify(value).length
 *       });
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterStorageHelper {
  /**
   * Gets a value from localStorage.
   *
   * Retrieves a string value from localStorage with SSR-safe checks.
   * Returns null if localStorage is unavailable or key doesn't exist.
   *
   * @param key - The localStorage key to retrieve
   * @returns The stored string value, or null if not found or unavailable
   *
   * @remarks
   * Safe to call in server-side rendering contexts. Always returns null
   * when localStorage is undefined (SSR, private browsing, etc.).
   *
   * @example
   * Override to add storage caching:
   * ```typescript
   * getLocalStorage(key) {
   *   // Check memory cache first
   *   const cached = this.storageCache.get(key);
   *   if (cached) return cached;
   *
   *   const value = super.getLocalStorage(key);
   *   if (value) this.storageCache.set(key, value);
   *   return value;
   * }
   * ```
   */
  getLocalStorage(key: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  }

  /**
   * Sets a value in localStorage.
   *
   * Stores a string value in localStorage with SSR-safe checks.
   * Silently fails if localStorage is unavailable.
   *
   * @param key - The localStorage key to set
   * @param value - The string value to store
   *
   * @remarks
   * Does not throw errors if storage is unavailable. Consider using
   * isLocalStorageAvailable() first for critical operations that need
   * to verify storage is accessible.
   *
   * @example
   * Override to add expiration timestamps:
   * ```typescript
   * setLocalStorage(key, value) {
   *   // Add expiration metadata
   *   const dataWithExpiry = JSON.stringify({
   *     value,
   *     expires: Date.now() + this.getTTL(key)
   *   });
   *   super.setLocalStorage(key, dataWithExpiry);
   * }
   * ```
   */
  setLocalStorage(key: string, value: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  }

  /**
   * Gets a value from sessionStorage with type-safe JSON parsing.
   *
   * Retrieves and parses a JSON value from sessionStorage. Returns the
   * fallback value if storage is unavailable, key doesn't exist, or
   * JSON parsing fails.
   *
   * @param key - The sessionStorage key to retrieve
   * @param fallback - Fallback value returned if key doesn't exist or parsing fails
   * @returns The parsed value of type T, or fallback
   *
   * @remarks
   * Parsing errors are silently caught and return the fallback. This ensures
   * graceful degradation when storage contains corrupted or invalid JSON.
   *
   * @example
   * Override to add validation:
   * ```typescript
   * getSessionStorage<T>(key, fallback) {
   *   const value = super.getSessionStorage(key, fallback);
   *   // Validate against schema
   *   if (!this.validateSchema(key, value)) {
   *     console.warn(`Invalid schema for ${key}, using fallback`);
   *     return fallback;
   *   }
   *   return value;
   * }
   * ```
   */
  getSessionStorage<T>(key: string, fallback: T): T {
    if (typeof sessionStorage === 'undefined') return fallback;

    const data = sessionStorage.getItem(key);
    if (!data) return fallback;

    try {
      return JSON.parse(data) as T;
    } catch {
      return fallback;
    }
  }

  /**
   * Sets a value in sessionStorage with JSON serialization.
   *
   * Serializes the value to JSON and stores it in sessionStorage.
   * Catches and logs errors from quota exceeded, circular references,
   * or private browsing mode restrictions.
   *
   * @param key - The sessionStorage key to set
   * @param value - The value to store (will be JSON stringified)
   *
   * @remarks
   * Errors are logged to console but don't throw. Common error scenarios:
   * - QuotaExceededError: Storage limit reached
   * - TypeError: Circular structure in value
   * - SecurityError: Private browsing mode restrictions
   *
   * @example
   * Override to add compression for large values:
   * ```typescript
   * setSessionStorage<T>(key, value) {
   *   const serialized = JSON.stringify(value);
   *   if (serialized.length > 10000) {
   *     // Compress large values
   *     const compressed = this.compress(serialized);
   *     super.setSessionStorage(key + ':compressed', compressed);
   *   } else {
   *     super.setSessionStorage(key, value);
   *   }
   * }
   * ```
   */
  setSessionStorage<T>(key: string, value: T): void {
    if (typeof sessionStorage === 'undefined') return;

    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      // Quota exceeded, circular structure, private mode, etc.
      console.error(`Failed to set sessionStorage for key "${key}"`, err);
    }
  }

  /**
   * Saves collection tag slug mapping to session storage.
   *
   * Maps URL slug keys to collection tag values for proper URL parsing
   * and navigation. Used when collections have tagged variants accessible
   * via /collections/handle/tag-slug URLs.
   *
   * @param key - The slug key from URL
   * @param value - The collection tag value
   *
   * @remarks
   * Maintains a mapping object in session storage that persists across
   * page navigations but clears on new browser sessions.
   *
   * @example
   * Override to add tag validation:
   * ```typescript
   * saveCollectionTagsSlugKey(key, value) {
   *   // Validate tag format before saving
   *   if (this.isValidCollectionTag(value)) {
   *     super.saveCollectionTagsSlugKey(key, value);
   *   } else {
   *     console.warn(`Invalid collection tag: ${value}`);
   *   }
   * }
   * ```
   */
  saveCollectionTagsSlugKey(key: string, value: string): void {
    const collectionTags = this.getSessionStorage(
      BOOST_SD_COLLECTION_TAGS_KEY,
      {} as Record<string, string>
    );
    collectionTags[key] = value;
    this.setSessionStorage(BOOST_SD_COLLECTION_TAGS_KEY, collectionTags);
  }

  /**
   * Saves collection ID mapping by handle to session storage.
   *
   * Maps collection handles to their numeric IDs for efficient lookup
   * during filter operations. Enables resolving collection IDs from
   * URL handles without additional API calls.
   *
   * @param handle - The collection handle (URL-friendly identifier)
   * @param value - The collection ID (numeric or string)
   *
   * @remarks
   * Collection handles are typically lowercase, hyphenated strings
   * (e.g., 'summer-sale'), while IDs are numeric identifiers.
   *
   * @example
   * Override to add analytics tracking:
   * ```typescript
   * saveCollectionIdByHandle(handle, value) {
   *   super.saveCollectionIdByHandle(handle, value);
   *   // Track collection mapping for analytics
   *   this.analytics.track('collection_mapped', {
   *     handle,
   *     id: value
   *   });
   * }
   * ```
   */
  saveCollectionIdByHandle(handle: string, value: string): void {
    const collectionIdsByHandle = this.getSessionStorage(
      BOOST_SD_COLLECTION_ID_KEY,
      {} as Record<string, string>
    );
    collectionIdsByHandle[handle] = value;
    this.setSessionStorage(BOOST_SD_COLLECTION_ID_KEY, collectionIdsByHandle);
  }

  /**
   * Gets collection tag from slug key.
   *
   * Retrieves the collection tag value associated with a URL slug key.
   * Returns undefined if the mapping doesn't exist.
   *
   * @param key - The slug key to lookup
   * @returns The collection tag value, or undefined if not found
   *
   * @example
   * Override to add fallback lookup:
   * ```typescript
   * getCollectionTagFromSlugKey(key) {
   *   const tag = super.getCollectionTagFromSlugKey(key);
   *   if (!tag) {
   *     // Try fetching from API if not in cache
   *     return this.fetchCollectionTagFromAPI(key);
   *   }
   *   return tag;
   * }
   * ```
   */
  getCollectionTagFromSlugKey(key: string): string | undefined {
    const collectionTags = this.getSessionStorage(
      BOOST_SD_COLLECTION_TAGS_KEY,
      {} as Record<string, string>
    );
    return collectionTags[key];
  }

  /**
   * Gets collection ID by handle key.
   *
   * Retrieves the collection ID associated with a collection handle.
   * Returns 0 if the mapping doesn't exist.
   *
   * @param key - The collection handle to lookup
   * @returns The collection ID, or 0 if not found
   *
   * @example
   * Override to add logging:
   * ```typescript
   * getCollectionIdByHandleKey(key) {
   *   const id = super.getCollectionIdByHandleKey(key);
   *   if (id === 0) {
   *     console.warn(`Collection ID not found for handle: ${key}`);
   *   }
   *   return id;
   * }
   * ```
   */
  getCollectionIdByHandleKey(key: string): string | number {
    const collectionIds = this.getSessionStorage(
      BOOST_SD_COLLECTION_ID_KEY,
      {} as Record<string, string | number>
    );
    return collectionIds[key] || 0;
  }

  /**
   * Sets the pagination page in session storage.
   *
   * Stores the current page number in session storage for persistence
   * across page reloads during the same browsing session. Validates
   * and normalizes page numbers.
   *
   * @param page - The page number to store (number or string)
   *
   * @remarks
   * Page numbers are validated and default to 1 if invalid (NaN, 0, negative).
   * Uses session storage so pagination state clears on new browser sessions.
   *
   * @example
   * Override to add page tracking:
   * ```typescript
   * setPaginationSession(page) {
   *   super.setPaginationSession(page);
   *   // Track pagination for analytics
   *   this.analytics.track('pagination_changed', {
   *     page: Number(page),
   *     timestamp: Date.now()
   *   });
   * }
   * ```
   */
  setPaginationSession(page: number | string): void {
    let pageNum = Number(page);

    if (!pageNum || Number.isNaN(pageNum)) {
      pageNum = 1;
    }

    // Use sessionStorage to store pagination state
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('boost-sd-initial-page', String(pageNum));
    }
  }

  /**
   * Gets the current pagination page from session storage.
   *
   * Retrieves the stored page number from session storage. Returns 1
   * if storage is unavailable, key doesn't exist, or value is invalid.
   *
   * @returns The stored page number, or 1 as default
   *
   * @remarks
   * Always returns a valid positive page number. Invalid values
   * (NaN, 0, negative) are normalized to 1.
   *
   * @example
   * Override to add default page per collection:
   * ```typescript
   * getPaginationSession() {
   *   const page = super.getPaginationSession();
   *   // Use custom default page for specific collections
   *   if (page === 1 && this.hasCustomDefaultPage()) {
   *     return this.getCustomDefaultPage();
   *   }
   *   return page;
   * }
   * ```
   */
  getPaginationSession(): number {
    if (typeof sessionStorage === 'undefined') return 1;

    const stored = sessionStorage.getItem('boost-sd-initial-page');
    if (!stored) return 1;

    const page = Number(stored);
    return !Number.isNaN(page) && page > 0 ? page : 1;
  }

  /**
   * Clears pagination from session storage.
   *
   * Removes the stored pagination state. Called when resetting
   * filters or navigating to a new collection.
   *
   * @example
   * Override to clear related pagination state:
   * ```typescript
   * clearPaginationSession() {
   *   super.clearPaginationSession();
   *   // Clear pagination-related caches
   *   this.clearPaginationCache();
   * }
   * ```
   */
  clearPaginationSession(): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem('boost-sd-initial-page');
  }

  /**
   * Removes an item from sessionStorage.
   *
   * Deletes a specific key from session storage with SSR-safe checks.
   *
   * @param key - The sessionStorage key to remove
   *
   * @example
   * Override to add removal tracking:
   * ```typescript
   * removeSessionStorage(key) {
   *   super.removeSessionStorage(key);
   *   this.trackStorageRemoval('session', key);
   * }
   * ```
   */
  removeSessionStorage(key: string): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(key);
  }

  /**
   * Removes an item from localStorage.
   *
   * Deletes a specific key from local storage with SSR-safe checks.
   *
   * @param key - The localStorage key to remove
   *
   * @example
   * Override to add removal tracking:
   * ```typescript
   * removeLocalStorage(key) {
   *   super.removeLocalStorage(key);
   *   this.trackStorageRemoval('local', key);
   * }
   * ```
   */
  removeLocalStorage(key: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key);
  }

  /**
   * Checks if localStorage is available and accessible.
   *
   * Tests localStorage availability by attempting to write and remove
   * a test value. Returns false in SSR contexts, private browsing mode,
   * or when storage is disabled.
   *
   * @returns True if localStorage is accessible and writable
   *
   * @remarks
   * Use this before critical storage operations that must succeed.
   * Note that availability can change during runtime (user disables storage).
   *
   * @example
   * Override to add custom storage detection:
   * ```typescript
   * isLocalStorageAvailable() {
   *   const available = super.isLocalStorageAvailable();
   *   if (available) {
   *     // Check custom storage quota requirements
   *     return this.hasEnoughStorageQuota();
   *   }
   *   return false;
   * }
   * ```
   */
  isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Checks if sessionStorage is available and accessible.
   *
   * Tests sessionStorage availability by attempting to write and remove
   * a test value. Returns false in SSR contexts, private browsing mode,
   * or when storage is disabled.
   *
   * @returns True if sessionStorage is accessible and writable
   *
   * @remarks
   * Use this before critical storage operations that must succeed.
   * Note that availability can change during runtime.
   *
   * @example
   * Override to add fallback storage:
   * ```typescript
   * isSessionStorageAvailable() {
   *   if (super.isSessionStorageAvailable()) {
   *     return true;
   *   }
   *   // Use memory fallback if session storage unavailable
   *   return this.enableMemoryFallback();
   * }
   * ```
   */
  isSessionStorageAvailable(): boolean {
    try {
      const test = '__sessionStorage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Clears all items from sessionStorage with a specific prefix.
   *
   * Removes all session storage keys that start with the given prefix.
   * Useful for clearing all filter-related storage at once.
   *
   * @param prefix - The key prefix to match (e.g., 'boost-sd-filter-')
   *
   * @remarks
   * Iterates through all storage keys to find matches. Performance scales
   * with total number of keys in storage, not just matching keys.
   *
   * @example
   * Override to add bulk clear tracking:
   * ```typescript
   * clearSessionStorageByPrefix(prefix) {
   *   const keyCount = this.countKeysWithPrefix('session', prefix);
   *   super.clearSessionStorageByPrefix(prefix);
   *   this.analytics.track('bulk_storage_clear', {
   *     type: 'session',
   *     prefix,
   *     keysCleared: keyCount
   *   });
   * }
   * ```
   */
  clearSessionStorageByPrefix(prefix: string): void {
    if (typeof sessionStorage === 'undefined') return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  /**
   * Clears all items from localStorage with a specific prefix.
   *
   * Removes all local storage keys that start with the given prefix.
   * Useful for clearing all filter-related persistent storage at once.
   *
   * @param prefix - The key prefix to match (e.g., 'boost-sd-filter-')
   *
   * @remarks
   * Iterates through all storage keys to find matches. Performance scales
   * with total number of keys in storage. Use carefully as this affects
   * persistent storage across sessions.
   *
   * @example
   * Override to add confirmation for critical prefixes:
   * ```typescript
   * clearLocalStorageByPrefix(prefix) {
   *   if (this.isCriticalPrefix(prefix)) {
   *     // Log before clearing critical storage
   *     this.logCriticalStorageClear(prefix);
   *   }
   *   super.clearLocalStorageByPrefix(prefix);
   * }
   * ```
   */
  clearLocalStorageByPrefix(prefix: string): void {
    if (typeof localStorage === 'undefined') return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
